import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Download, Target, Handshake, BookOpen, Clock, Award, 
  Users, Briefcase, CheckCircle2, GraduationCap, TrendingUp,
  FileText, Brain, Presentation, Monitor, ChevronDown, ChevronRight
} from 'lucide-react';
import { useState } from 'react';

const PROGRAM_PDF_URL = "https://customer-assets.emergentagent.com/job_cfa-portal/artifacts/5xc8fczo_Programme_NTC_RNCP39063_v2.docx";

// Données du programme
const programData = {
  title: "TP Négociateur Technico-Commercial",
  rncp: "RNCP39063",
  level: "Niveau 5 (Bac+2)",
  duration: "490h centre + 350h minimum entreprise",
  
  objectives: [
    "Élaborer et mettre en œuvre une stratégie de prospection commerciale",
    "Négocier des solutions techniques et commerciales",
    "Fidéliser la clientèle et optimiser la relation client",
    "Représenter l'entreprise et valoriser son image",
    "Intégrer une démarche de veille continue sur le marché",
    "Adopter une posture d'expert-conseil en vente B2B"
  ],
  
  transversalSkills: [
    "Communiquer efficacement",
    "Adopter un comportement orienté client",
    "Rechercher un accord gagnant-gagnant",
    "Évaluer ses actions commerciales",
    "Résolution de problèmes complexes"
  ],
  
  ccp1: {
    title: "Élaborer une stratégie de prospection et la mettre en œuvre",
    hours: "245h",
    competences: [
      {
        name: "Assurer une veille commerciale",
        hours: "77h",
        modules: [
          { code: "M1.1", name: "Environnement commercial BtoB", hours: "14h" },
          { code: "M1.2", name: "Méthodes et sources de veille", hours: "14h" },
          { code: "M1.3", name: "Collecte et analyse des données", hours: "14h" },
          { code: "M1.4", name: "Analyse concurrentielle", hours: "14h" },
          { code: "M1.5", name: "Anticipation des évolutions de marché", hours: "14h" },
          { code: "M1.6", name: "Communication et partage des résultats", hours: "7h" }
        ]
      },
      {
        name: "Concevoir et organiser un plan d'actions commerciales",
        hours: "105h",
        modules: [
          { code: "M2.1", name: "Analyse de marché et segmentation", hours: "21h" },
          { code: "M2.2", name: "Personas et ciblage", hours: "21h" },
          { code: "M2.3", name: "Stratégie produit/service et positionnement", hours: "14h" },
          { code: "M2.4", name: "Élaboration du plan d'actions", hours: "21h" },
          { code: "M2.5", name: "Stratégie de communication omnicanale", hours: "14h" },
          { code: "M2.6", name: "Suivi, évaluation et ajustement", hours: "14h" }
        ]
      },
      {
        name: "Prospecter un secteur défini",
        hours: "91h",
        modules: [
          { code: "M3.1", name: "Méthodes de prospection multicanale", hours: "28h" },
          { code: "M3.2", name: "Qualification et prise de rendez-vous", hours: "21h" },
          { code: "M3.3", name: "CRM, e-CRM et automatisation", hours: "21h" },
          { code: "M3.4", name: "Social selling et personal branding", hours: "14h" },
          { code: "M3.5", name: "Évaluation et ajustement des actions", hours: "7h" }
        ]
      },
      {
        name: "Analyser ses performances et mettre en œuvre des actions correctives",
        hours: "49h",
        modules: [
          { code: "M4.1", name: "Tableaux de bord commerciaux", hours: "14h" },
          { code: "M4.2", name: "Analyse des écarts et SWOT", hours: "14h" },
          { code: "M4.3", name: "Plan d'actions correctives", hours: "14h" },
          { code: "M4.4", name: "Présentation orale des résultats", hours: "7h" }
        ]
      }
    ]
  },
  
  ccp2: {
    title: "Négocier une solution technique et commerciale et consolider l'expérience client",
    hours: "245h",
    competences: [
      {
        name: "Représenter l'entreprise et valoriser son image",
        hours: "35h",
        modules: [
          { code: "M5.1", name: "Image professionnelle et posture", hours: "7h" },
          { code: "M5.2", name: "Personal branding et réseaux sociaux", hours: "14h" },
          { code: "M5.3", name: "Argumentaire produit/service – CAP", hours: "14h" }
        ]
      },
      {
        name: "Concevoir une proposition technique et commerciale",
        hours: "70h",
        modules: [
          { code: "M6.1", name: "Découverte et analyse des besoins", hours: "21h" },
          { code: "M6.2", name: "Analyse de la concurrence et de l'offre", hours: "14h" },
          { code: "M6.3", name: "Chiffrage et construction du prix", hours: "14h" },
          { code: "M6.4", name: "Rédaction de la proposition commerciale", hours: "21h" }
        ]
      },
      {
        name: "Négocier une solution technique et commerciale",
        hours: "77h",
        modules: [
          { code: "M7.1", name: "Techniques de négociation", hours: "21h" },
          { code: "M7.2", name: "Traitement des objections", hours: "21h" },
          { code: "M7.3", name: "Présentation et démonstration", hours: "21h" },
          { code: "M7.4", name: "Closing et conclusion", hours: "14h" }
        ]
      },
      {
        name: "Réaliser le bilan et ajuster son activité commerciale",
        hours: "35h",
        modules: [
          { code: "M8.1", name: "Bilan d'activité et reporting", hours: "14h" },
          { code: "M8.2", name: "Identification des opportunités", hours: "14h" },
          { code: "M8.3", name: "Aspects juridiques du contrat de vente", hours: "7h" }
        ]
      },
      {
        name: "Optimiser la gestion de la relation client",
        hours: "56h",
        modules: [
          { code: "M9.1", name: "Suivi et fidélisation client", hours: "21h" },
          { code: "M9.2", name: "CRM/e-CRM et automatisation", hours: "14h" },
          { code: "M9.3", name: "Mesure satisfaction et amélioration", hours: "14h" },
          { code: "M9.4", name: "Partenariats et synergies", hours: "7h" }
        ]
      }
    ]
  },
  
  pedagogy: [
    { icon: BookOpen, name: "Apports théoriques", desc: "Cours interactifs, supports projetés, fiches mémo", percent: "15%" },
    { icon: FileText, name: "Études de cas", desc: "Cas d'entreprises BtoB réels ou fictifs", percent: "30%" },
    { icon: Presentation, name: "Jeux de rôle", desc: "Simulations prospection, négociation enregistrées", percent: "30%" },
    { icon: Monitor, name: "Travaux pratiques", desc: "CRM (HubSpot/Salesforce), LinkedIn, Excel/Power BI", percent: "20%" },
    { icon: Award, name: "Entraînements examens", desc: "MSP blanches, entretiens techniques", percent: "5%" }
  ],
  
  careers: [
    { title: "Technico-commercial(e)", sector: "Industrie, BTP, Tech, Distribution", exp: "Débutant" },
    { title: "Ingénieur(e) d'affaires junior", sector: "Services, Conseil, IT, SaaS", exp: "Débutant" },
    { title: "Chef(fe) de secteur", sector: "Distribution, Pharma, Négoce", exp: "Débutant" },
    { title: "Attaché(e) commercial(e) BtoB", sector: "Tout secteur BtoB", exp: "Débutant" },
    { title: "Chargé(e) de développement", sector: "PME/ETI", exp: "1-2 ans" },
    { title: "Key Account Manager junior", sector: "Grands comptes BtoB", exp: "2-3 ans" }
  ]
};

// Composant pour afficher un bloc de compétences
const CompetenceBlock = ({ competence, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-[#E6E6E6] bg-white mb-4">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-[#F5F5F5] transition-colors text-left"
        data-testid={`competence-toggle-${index}`}
      >
        <div className="flex items-center gap-4">
          <span className="bg-[#C8A75A] text-[#0B1F3A] px-3 py-1 text-sm font-semibold">
            {competence.hours}
          </span>
          <span className="font-medium text-[#0B1F3A]">{competence.name}</span>
        </div>
        {isOpen ? <ChevronDown size={20} className="text-[#C8A75A]" /> : <ChevronRight size={20} className="text-[#4A4A4A]" />}
      </button>
      
      {isOpen && (
        <div className="px-4 pb-4 border-t border-[#E6E6E6]">
          <div className="grid gap-2 mt-4">
            {competence.modules.map((module, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-4 bg-[#F5F5F5]">
                <div className="flex items-center gap-3">
                  <span className="text-[#C8A75A] font-mono text-sm">{module.code}</span>
                  <span className="text-[#0B1F3A] text-sm">{module.name}</span>
                </div>
                <span className="text-[#4A4A4A] text-sm">{module.hours}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Programme = () => {
  return (
    <div data-testid="programme-page">
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-20" data-testid="programme-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Programme détaillé
            </span>
            <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
              {programData.title}
            </h1>
            <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/10 px-4 py-2 text-white text-sm">
                <span className="text-[#C8A75A] font-semibold">{programData.rncp}</span>
              </div>
              <div className="bg-white/10 px-4 py-2 text-white text-sm">
                <span className="text-[#C8A75A] font-semibold">{programData.level}</span>
              </div>
              <div className="bg-white/10 px-4 py-2 text-white text-sm">
                <span className="text-[#C8A75A] font-semibold">490h</span> centre
              </div>
            </div>

            <a 
              href={PROGRAM_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-6 py-3 transition-colors font-semibold"
              data-testid="download-program"
            >
              <Download size={20} />
              Télécharger le programme complet
            </a>
          </div>
        </div>
      </section>

      {/* Objectifs */}
      <section className="py-20 bg-white" data-testid="objectives-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Objectifs
              </span>
              <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-6">
                Ce que vous allez apprendre
              </h2>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-8"></div>
              
              <div className="space-y-4">
                {programData.objectives.map((obj, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#C8A75A] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-[#4A4A4A]">{obj}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#F5F5F5] p-8 border border-[#E6E6E6]">
              <h3 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-6">
                Compétences transversales
              </h3>
              <div className="space-y-3">
                {programData.transversalSkills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white">
                    <Brain className="text-[#C8A75A]" size={18} />
                    <span className="text-[#0B1F3A] font-medium">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CCP1 */}
      <section className="py-20 bg-[#F5F5F5]" data-testid="ccp1-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-[#0B1F3A] p-4">
              <Target className="text-[#C8A75A]" size={32} />
            </div>
            <div>
              <span className="text-[#C8A75A] font-semibold text-sm">CCP1 • {programData.ccp1.hours}</span>
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A]">
                {programData.ccp1.title}
              </h2>
            </div>
          </div>
          
          <div>
            {programData.ccp1.competences.map((comp, index) => (
              <CompetenceBlock key={index} competence={comp} index={`ccp1-${index}`} />
            ))}
          </div>
        </div>
      </section>

      {/* CCP2 */}
      <section className="py-20 bg-white" data-testid="ccp2-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-[#0B1F3A] p-4">
              <Handshake className="text-[#C8A75A]" size={32} />
            </div>
            <div>
              <span className="text-[#C8A75A] font-semibold text-sm">CCP2 • {programData.ccp2.hours}</span>
              <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A]">
                {programData.ccp2.title}
              </h2>
            </div>
          </div>
          
          <div>
            {programData.ccp2.competences.map((comp, index) => (
              <CompetenceBlock key={index} competence={comp} index={`ccp2-${index}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Méthodes pédagogiques */}
      <section className="py-20 bg-[#0B1F3A]" data-testid="pedagogy-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Pédagogie
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-white">
              Méthodes d'enseignement
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {programData.pedagogy.map((method, index) => (
              <div key={index} className="bg-white/5 backdrop-blur border border-white/10 p-6 text-center">
                <method.icon className="mx-auto text-[#C8A75A] mb-4" size={32} />
                <div className="text-[#C8A75A] text-2xl font-semibold mb-2">{method.percent}</div>
                <h3 className="text-white font-semibold mb-2">{method.name}</h3>
                <p className="text-[#F5F5F5]/70 text-sm">{method.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Débouchés */}
      <section className="py-20 bg-[#F5F5F5]" data-testid="careers-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Débouchés
            </span>
            <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A]">
              Métiers accessibles après la formation
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programData.careers.map((career, index) => (
              <div key={index} className="bg-white border border-[#E6E6E6] p-6 hover:border-[#C8A75A] transition-colors">
                <Briefcase className="text-[#C8A75A] mb-4" size={24} />
                <h3 className="font-semibold text-[#0B1F3A] mb-2">{career.title}</h3>
                <p className="text-[#4A4A4A] text-sm mb-3">{career.sector}</p>
                <span className="inline-block bg-[#0B1F3A] text-[#C8A75A] text-xs px-2 py-1">
                  {career.exp}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif-heading text-3xl font-semibold text-[#0B1F3A] mb-6">
            Prêt à vous lancer ?
          </h2>
          <p className="text-[#4A4A4A] mb-8 max-w-2xl mx-auto">
            Rejoignez l'Institut BIA et devenez un expert de la vente B2B. 
            Formation 100% gratuite et rémunérée dès le premier jour.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/etudiants#candidature">
              <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6 text-base font-semibold" data-testid="cta-apply">
                Candidater maintenant
              </Button>
            </Link>
            <a 
              href={PROGRAM_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-none px-8 py-6 text-base font-medium transition-colors"
              data-testid="cta-download"
            >
              <Download size={18} />
              Télécharger le programme
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programme;
